import type { ReactNode } from 'react';
import type { Theme } from '@mui/material/styles';

import {
  File,
  Music,
  Video,
  FileText,
  FileType,
  Presentation,
  FileSpreadsheet,
  Image as ImageIcon,
} from 'lucide-react';

import { Typography } from '@mui/material';

import type { DocumentType } from './documents-data';

// Icon + badge colours per document type. Colours pull from the MUI palette
// (or common.white/black for pdf's white-on-red badge) so they track the
// theme rather than hardcoding hex values. Labels are short extension-style
// abbreviations (not the real filename extension in every case) so
// "{label} · {size}" fits on one line at the smallest grid width.
// pdf's badge is just the word "PDF", not a pictogram — a stand-alone glyph
// read as clutter at 26px, and there's no unbranded "PDF file" icon that
// doesn't also risk looking like Adobe's Acrobat mark.

// Image and video share the neutral glyph colour; every other type is mixed
// towards it so the badges read as one set instead of six unrelated brights.
export const GLYPH_NEUTRAL = (theme: Theme) => theme.palette.grey[400];
const harmonized = (color: (theme: Theme) => string) => (theme: Theme) =>
  `color-mix(in srgb, ${color(theme)} 70%, ${GLYPH_NEUTRAL(theme)})`;

/** The file's own extension, as ".docx", or the type's label when the name has none. */
export function extensionLabel(name: string, type: DocumentType): string {
  const extension = /\.([\p{L}\p{N}]{1,5})$/u.exec(name);
  return extension ? `.${extension[1].toLowerCase()}` : DOCUMENT_TYPE_META[type].label;
}

export const DOCUMENT_TYPE_META: Record<
  DocumentType,
  {
    renderIcon: (size: number) => ReactNode;
    label: string;
    badgeColor: string;
    iconColor: (theme: Theme) => string;
  }
> = {
  pdf: {
    renderIcon: () => (
      <Typography component="span" sx={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.02em', lineHeight: 1 }}>
        PDF
      </Typography>
    ),
    label: '.pdf',
    badgeColor: 'error.main',
    // The only badge with its own colour, so its label keeps full contrast.
    iconColor: (theme) => theme.palette.common.white,
  },
  spreadsheet: {
    renderIcon: (size) => <FileSpreadsheet size={size} strokeWidth={2} />,
    label: '.xls',
    badgeColor: 'grey.800',
    iconColor: harmonized((theme) => theme.palette.success.main),
  },
  document: {
    renderIcon: (size) => <FileType size={size} strokeWidth={2} />,
    label: '.doc',
    badgeColor: 'grey.800',
    iconColor: harmonized((theme) => theme.palette.info.main),
  },
  presentation: {
    renderIcon: (size) => <Presentation size={size} strokeWidth={2} />,
    label: '.ppt',
    badgeColor: 'grey.800',
    iconColor: harmonized((theme) => theme.palette.error.main),
  },
  text: {
    renderIcon: (size) => <FileText size={size} strokeWidth={2} />,
    label: '.txt',
    badgeColor: 'grey.800',
    iconColor: harmonized((theme) => theme.palette.warning.main),
  },
  image: {
    renderIcon: (size) => <ImageIcon size={size} strokeWidth={2} />,
    label: '.jpg',
    badgeColor: 'grey.800',
    iconColor: GLYPH_NEUTRAL,
  },
  video: {
    renderIcon: (size) => <Video size={size} strokeWidth={2} />,
    label: '.mp4',
    badgeColor: 'grey.800',
    iconColor: GLYPH_NEUTRAL,
  },
  audio: {
    renderIcon: (size) => <Music size={size} strokeWidth={2} />,
    label: '.mp3',
    badgeColor: 'grey.800',
    iconColor: GLYPH_NEUTRAL,
  },
  other: {
    renderIcon: (size) => <File size={size} strokeWidth={2} />,
    label: 'File',
    badgeColor: 'grey.800',
    iconColor: GLYPH_NEUTRAL,
  },
};
